package com.isaias.fyattravel.web.pages.payment

import androidx.compose.runtime.*
import kotlinx.browser.window
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import com.isaias.fyattravel.web.i18n.WebStringKey
import com.isaias.fyattravel.web.i18n.WebStrings
import com.isaias.fyattravel.web.theme.ThemeColors

/**
 * Payment Success Page
 * Shows after successful PesaPal payment
 */
@Composable
fun PaymentSuccessPage(onNavigate: (String) -> Unit) {
    // Get order details from localStorage
    val orderId = window.localStorage.getItem("pesapal_order_id") ?: ""
    val productId = window.localStorage.getItem("pesapal_product_id") ?: ""
    val customerName = window.localStorage.getItem("pesapal_customer_name") ?: ""

    // Clear stored data after reading
    LaunchedEffect(Unit) {
        window.localStorage.removeItem("pesapal_order_id")
        window.localStorage.removeItem("pesapal_product_id")
        window.localStorage.removeItem("pesapal_customer_name")
    }

    Div({
        style {
            maxWidth(600.px)
            property("margin", "0 auto")
            padding(3.em, 1.em)
            textAlign("center")
        }
    }) {
        // Success icon
        Div({
            style {
                width(100.px)
                height(100.px)
                borderRadius(50.percent)
                backgroundColor(ThemeColors.successBg)
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.Center)
                property("margin", "0 auto 2em")
            }
        }) {
            Span({
                style {
                    fontSize(3.em)
                    color(ThemeColors.success)
                }
            }) { Text("✓") }
        }

        H1({
            style {
                fontSize(2.em)
                fontWeight("800")
                color(ThemeColors.textPrimary)
                marginBottom(0.5.em)
            }
        }) { Text(WebStrings.t(WebStringKey.PAYMENT_SUCCESS_TITLE)) }

        P({
            style {
                fontSize(1.1.em)
                color(ThemeColors.textMuted)
                marginBottom(2.em)
            }
        }) {
            val name = customerName.ifBlank { "" }
            if (name.isNotBlank()) {
                Text(WebStrings.format(WebStringKey.PAYMENT_SUCCESS_MESSAGE_WITH_NAME, name))
            } else {
                Text(WebStrings.t(WebStringKey.PAYMENT_SUCCESS_MESSAGE_GENERIC))
            }
        }

        // Order details card
        Div({
            style {
                backgroundColor(ThemeColors.surface)
                padding(1.5.em)
                borderRadius(1.em)
                textAlign("left")
                marginBottom(2.em)
            }
        }) {
            H2({
                style {
                    fontSize(1.25.em)
                    fontWeight("700")
                    color(ThemeColors.textPrimary)
                    marginBottom(1.em)
                }
            }) { Text(WebStrings.t(WebStringKey.ORDER_DETAILS)) }

            if (orderId.isNotBlank()) {
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        justifyContent(JustifyContent.SpaceBetween)
                        padding(0.75.em, 0.px)
                        property("border-bottom", "1px solid ${ThemeColors.divider}")
                    }
                }) {
                    Span({ style { color(ThemeColors.textMuted) } }) { Text(WebStrings.t(WebStringKey.ORDER_ID_LABEL)) }
                    Span({ style { fontWeight("600"); color(ThemeColors.textPrimary) } }) { Text(orderId) }
                }
            }

            Div({
                style {
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.SpaceBetween)
                    padding(0.75.em, 0.px)
                        property("border-bottom", "1px solid ${ThemeColors.divider}")
                }
            }) {
                Span({ style { color(ThemeColors.textMuted) } }) { Text(WebStrings.t(WebStringKey.STATUS_LABEL)) }
                Span({ 
                    style { 
                        fontWeight("600")
                        color(ThemeColors.success)
                        backgroundColor(ThemeColors.successBg)
                        padding(0.25.em, 0.75.em)
                        borderRadius(0.5.em)
                    } 
                }) { Text(WebStrings.t(WebStringKey.PAYMENT_STATUS_PAID)) }
            }

            Div({
                style {
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.SpaceBetween)
                    padding(0.75.em, 0.px)
                }
            }) {
                Span({ style { color(ThemeColors.textMuted) } }) { Text(WebStrings.t(WebStringKey.PAYMENT_METHOD_LABEL)) }
                Span({ style { fontWeight("600"); color(ThemeColors.textPrimary) } }) { Text(WebStrings.t(WebStringKey.PAYMENT_METHOD_PESAPAL)) }
            }
        }

        P({
            style {
                fontSize(0.95.em)
                color(ThemeColors.textMuted)
                marginBottom(2.em)
            }
        }) {
            Text(WebStrings.t(WebStringKey.CONFIRMATION_EMAIL_NOTE))
        }

        // Action buttons
        Div({
            style {
                display(DisplayStyle.Flex)
                gap(1.em)
                justifyContent(JustifyContent.Center)
            }
        }) {
            Button({
                style {
                    backgroundColor(ThemeColors.primary)
                    color(ThemeColors.textOnGold)
                    padding(0.875.em, 1.5.em)
                    borderRadius(0.75.em)
                    border(0.px)
                    cursor("pointer")
                    fontWeight("700")
                    fontSize(1.em)
                }
                onClick { onNavigate("/categories") }
            }) {
                Text(WebStrings.t(WebStringKey.BROWSE_MORE_PRODUCTS))
            }

            Button({
                style {
                    backgroundColor(ThemeColors.surface)
                    color(ThemeColors.textPrimary)
                    padding(0.875.em, 1.5.em)
                    borderRadius(0.75.em)
                    property("border", "1px solid ${ThemeColors.divider}")
                    cursor("pointer")
                    fontWeight("700")
                    fontSize(1.em)
                }
                onClick { onNavigate("/") }
            }) {
                Text(WebStrings.t(WebStringKey.GO_TO_HOME))
            }
        }

        // Contact support
        P({
            style {
                marginTop(2.em)
                fontSize(0.9.em)
                color(ThemeColors.textSecondary)
            }
        }) {
            Text(WebStrings.t(WebStringKey.NEED_HELP_CONTACT))
        }
    }
}
